import React from 'react';
import { Input } from 'components';
import { ReferralService } from 'services';
import { Globals } from 'utils';

interface Props {
  balance: number;
  hasPaymentMobile: boolean;
  onGoToProfile: () => void;
  onSuccess: () => void;
  onClose: () => void;
}

interface State {
  amountText: string;
  submitting: boolean;
}

// Modal "¿Cuánto retiras?" -- equivalente web de la pantalla "Retirar" de
// Mobile (src/screens/referral/withdrawal.tsx). Mismas 4 reglas:
// 1. Sin pago móvil -> input deshabilitado, mensaje rojo fijo, botón cambia
//    a "Ir a mi perfil".
// 2. Con pago móvil, campo vacío -> botón deshabilitado, sin mensaje.
// 3. Con pago móvil, monto > balance -> mensaje rojo de saldo insuficiente,
//    botón deshabilitado.
// 4. Con pago móvil, 0 < monto <= balance -> botón habilitado.
class ModalWithdrawal extends React.Component<Props, State> {
  state: State = {
    amountText: '',
    submitting: false,
  };

  get amount(): number {
    return parseFloat(this.state.amountText);
  }

  get hasValidAmount(): boolean {
    const { amountText } = this.state;
    return amountText.trim() !== '' && !Number.isNaN(this.amount) && this.amount > 0;
  }

  get exceedsBalance(): boolean {
    return this.hasValidAmount && this.amount > this.props.balance;
  }

  get canSubmit(): boolean {
    return (
      this.props.hasPaymentMobile &&
      this.hasValidAmount &&
      !this.exceedsBalance &&
      !this.state.submitting
    );
  }

  get errorMessage(): string | null {
    if (!this.props.hasPaymentMobile) {
      return 'No tienes datos de pago móvil configurados. Agrégalos en tu perfil para continuar.';
    }
    if (this.exceedsBalance) {
      return 'El monto ingresado supera tu saldo disponible.';
    }
    return null;
  }

  setMax = () => {
    this.setState({ amountText: this.props.balance.toString() });
  };

  submit = async () => {
    if (!this.canSubmit) return;
    this.setState({ submitting: true });
    try {
      await ReferralService.client.requestWithdrawal({ amount: this.amount });
      Globals.showSuccess('Solicitud de retiro enviada correctamente');
      this.props.onSuccess();
    } catch (err: any) {
      const data = err?.response?.data;
      const message =
        data?.error?.message ||
        (typeof data?.message === 'string' ? data.message : null) ||
        'No se pudo procesar la solicitud';
      Globals.showError(message);
    } finally {
      this.setState({ submitting: false });
    }
  };

  render() {
    const { hasPaymentMobile, balance, onGoToProfile, onClose } = this.props;
    const { amountText, submitting } = this.state;

    return (
      <div id='modal-withdrawal-referral'>
        <p className='question'>¿Cuánto retiras?</p>
        <p className='subtitle'>
          Después de solicitar el retiro, este le llegará a su banco en un lapso de 48h hábiles
        </p>

        <div className='input-wrapper'>
          <Input
            name='amount'
            type='decimal'
            placeholder='xx.xxx.xxx'
            value={amountText}
            disabled={!hasPaymentMobile || submitting}
            onChange={(e: any) => this.setState({ amountText: e.target.value })}
          />
          {hasPaymentMobile && (
            <button className='btn-max' disabled={submitting} onClick={this.setMax} type='button'>
              MAX
            </button>
          )}
        </div>
        <p className='balance-text'>Saldo actual: T {balance.toFixed(0)}</p>

        {this.errorMessage && <p className='error-text'>{this.errorMessage}</p>}

        {!hasPaymentMobile ? (
          <button className='btn btn-primary' onClick={onGoToProfile}>
            Ir a mi perfil
          </button>
        ) : (
          <button className='btn btn-primary' disabled={!this.canSubmit} onClick={this.submit}>
            {submitting ? 'Enviando...' : 'Siguiente'}
          </button>
        )}

        <button className='btn-cancel' disabled={submitting} onClick={onClose}>
          Cancelar
        </button>
      </div>
    );
  }
}

export default ModalWithdrawal;
